import type React from 'react'
import { useCallback, useEffect, useRef } from 'react'
import CloseIcon from 'components/icons/CloseIcon'
import { createPortal } from 'react-dom'
import './ModalWindow.scss'

export interface ModalWindowProps {
  isOpened: boolean
  isCloseButton?: boolean
  className?: string
  classNameContainer?: string
  dataTest?: string
  children?: React.ReactNode
  closeButtonPlacement?: { top?: string; right?: string; bottom?: string; left?: string }
  submitButtonRef?: React.RefObject<HTMLButtonElement>
  onCloseModal?: () => void
  title?: string
  backgroundColor?: string
}

const ModalWindow: React.FC<ModalWindowProps> = (props) => {
  const {
    isOpened,
    isCloseButton,
    onCloseModal,
    children,
    className,
    classNameContainer,
    dataTest,
    submitButtonRef,
    title,
    backgroundColor,
    closeButtonPlacement,
  } = props

  const overlayRef = useRef<HTMLDivElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        const allOverlays = document.querySelectorAll('.modal__overlay')
        const topOverlay = allOverlays[allOverlays.length - 1]
        if (overlayRef.current !== topOverlay) return // not top → do nothing
        event.stopImmediatePropagation() // top → stop others from firing
        onCloseModal?.()
        return
      }
      if (submitButtonRef?.current && event.key === 'Enter') {
        event.preventDefault()
        submitButtonRef.current.click()
      }
    },
    [onCloseModal, submitButtonRef],
  )

  // Prevent body scroll when open
  useEffect(() => {
    if (!isOpened) return
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prevOverflow
    }
  }, [isOpened])

  // Key handling
  useEffect(() => {
    if (!isOpened) return
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpened, handleKeyDown])

  if (!isOpened) return null

  const content = (
    <div
      ref={overlayRef}
      className="modal__overlay"
      // Matching original: ignore backdrop clicks (do nothing)
      onMouseDown={(e) => {
        // if you ever want to close on backdrop click, check e.target === overlayRef.current and call onCloseModal()
      }}
      data-test={dataTest}
      aria-hidden={false}
    >
      <div
        ref={panelRef}
        className={`modal__container modal__panel ${classNameContainer ?? ''} ${className ?? ''}`}
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()} // prevent overlay handlers
        style={{ backgroundColor: backgroundColor || undefined }}
      >
        {title ? (
          <div className="modal__header">
            <h2 data-test="modal-title">{title}</h2>
            {isCloseButton && (
              <button
                type="button"
                className="modal__close"
                style={closeButtonPlacement}
                onClick={onCloseModal}
                data-test="close-modal-button"
                aria-label="Close modal"
              >
                <CloseIcon />
              </button>
            )}
          </div>
        ) : (
          isCloseButton && (
            <button
              type="button"
              className="modal__close"
              style={closeButtonPlacement}
              onClick={onCloseModal}
              data-test="close-modal-button"
              aria-label="Close modal"
            >
              <CloseIcon />
            </button>
          )
        )}
        <div className="modal__body">
          {children}
        </div>
      </div>
    </div>
  )

  // Portal to body for proper stacking, similar to MUI Modal
  return createPortal(content, document.body)
}

export default ModalWindow
